import dayjs from "dayjs"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useSelector } from "react-redux"
import { Link } from "react-router-dom"
import CommonSelect from "../../../core/common/commonSelect"
import PredefinedDateRanges from "../../../core/common/datePicker"
import TooltipOption from "../../../core/common/tooltipOption"
import { selectSystemsForCurrentBranch } from "../../../core/data/redux/slice/systemsSlice"
import { selectCurrentSession } from "../../../core/data/redux/slice/sessionsSlice"
import { selectUser } from "../../../core/data/redux/slice/user"
import { all_routes } from "../../router/all_routes"
import { getAllInvoicesForPrint, getCashiersList, CashierListItem } from "../../../service/fee-invoices"
import { getAllFeeGroups, FEE_GROUP } from "../../../service/fee-group"
import { SYSTEM_ROLES } from "../../../constant"
import { toast } from "react-toastify"
import {
  computeCashierReportTotals,
  CashierReportTables,
} from "../../accounts/cashierReportShared"
import { InvoiceTableData } from "../../accounts/accounts-invoices"
import InvoicesPrintModal from "../../accounts/invoicesPrintModal"

const routes = all_routes

const CashierReport = () => {
  const systems = useSelector(selectSystemsForCurrentBranch)
  const currentSession = useSelector(selectCurrentSession)
  const user = useSelector(selectUser)
  const isCashier = user?.role?.name?.toLowerCase?.() === SYSTEM_ROLES.cashier

  const [invoices, setInvoices] = useState<InvoiceTableData[]>([])
  const [loading, setLoading] = useState(true)
  const [feeGroups, setFeeGroups] = useState<FEE_GROUP[]>([])
  const [cashiers, setCashiers] = useState<CashierListItem[]>([])
  const [showPrintModal, setShowPrintModal] = useState(false)
  const [exportType, setExportType] = useState<"pdf" | "excel">("pdf")

  const [filters, setFilters] = useState<{
    startDate?: string
    endDate?: string
    system?: string
    feeGroup?: string
    session?: string
    collectedBy?: string
  }>({
    system: systems[0]?._id,
    session: currentSession?._id,
    // Must be a full day range, not the exact current instant — the date
    // picker widget normalizes "Today" to startOf/endOf day when picked
    // through its own UI (that's why re-selecting Today after switching
    // away fixes it), but this initial state bypasses that entirely. Two
    // bare `dayjs()` calls a few ms apart matched ~nothing, showing 0 data
    // on first load every time.
    startDate: dayjs().startOf("day").toISOString(),
    endDate: dayjs().endOf("day").toISOString(),
  })

  useEffect(() => {
    setFilters((prev) => ({ ...prev, session: currentSession?._id }))
  }, [currentSession?._id])

  useEffect(() => {
    getAllFeeGroups({ limit: 100 })
      .then((res) => setFeeGroups(res.data))
      .catch(() => {})
  }, [])

  // A cashier's own collections are no longer scoped server-side by default
  // (so a colleague can cover for an absent cashier, see routes/invoice.js) --
  // so this page needs the same per-cashier filter for a cashier login as it
  // already has for admin, not just a combined "everyone" total.
  // getAllRoles/getAllUsers (the old way this list was built) both need
  // admin-only user.view/role.view permission, so a cashier's own call
  // 403'd silently and the dropdown only ever showed "All Cashiers" for
  // them -- this dedicated endpoint is gated by the same fees permission
  // the report itself already requires, so it works for a cashier too.
  useEffect(() => {
    getCashiersList()
      .then((res) => setCashiers(res.data))
      .catch(() => {})
  }, [])

  // Default a cashier's own view to their own collections (matches the old
  // server-side-scoped behavior) -- they can still switch to "All Cashiers"
  // or a colleague's name from the dropdown when covering for someone absent.
  useEffect(() => {
    if (isCashier && user?._id) {
      setFilters((prev) => (prev.collectedBy ? prev : { ...prev, collectedBy: user._id }))
    }
  }, [isCashier, user?._id])

  const fetchInvoices = useCallback(async () => {
    setLoading(true)
    try {
      const response = await getAllInvoicesForPrint({
        status: "paid",
        system: filters.system,
        session: filters.session,
        feeGroup: filters.feeGroup,
        startDate: filters.startDate,
        endDate: filters.endDate,
        collectedBy: filters.collectedBy,
      })
      if (response.success) {
        setInvoices(response.data)
      }
    } catch (error) {
      console.error("Error fetching cashier report:", error)
      toast.error("Failed to load cashier report")
      setInvoices([])
    } finally {
      setLoading(false)
    }
  }, [filters])

  useEffect(() => {
    fetchInvoices()
  }, [fetchInvoices])

  const totals = useMemo(() => computeCashierReportTotals(invoices), [invoices])

  const printRef = useRef<HTMLDivElement>(null)

  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">Cashier Report</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to="#">Reports</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Cashier Report
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <TooltipOption
                onRefresh={fetchInvoices}
                onPrint={() => {
                  setExportType("pdf")
                  setShowPrintModal(true)
                }}
                onExportPdf={() => {
                  setExportType("pdf")
                  setShowPrintModal(true)
                }}
                onExportExcel={() => {
                  setExportType("excel")
                  setShowPrintModal(true)
                }}
              />
            </div>
          </div>

          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
              <h4 className="mb-3">Collections</h4>

              <div className="d-flex align-items-center flex-wrap">
                <div className="mb-3 me-2">
                  <PredefinedDateRanges
                    defaultStartDate={new Date()}
                    defaultEndDate={new Date()}
                    onDateSelect={({ startDate, endDate }) => {
                      setFilters((prev) => ({ ...prev, startDate, endDate }))
                    }}
                  />
                </div>

                <div className="mb-3 me-2">
                  <CommonSelect
                    className="select"
                    options={systems}
                    value={systems.find((sys: any) => sys._id === filters.system)}
                    getOptionValue={(item: any) => item._id}
                    getOptionLabel={(item: any) => item.name}
                    onChange={(item: any) =>
                      setFilters((prev) => ({ ...prev, system: item?._id }))
                    }
                    placeholder="Select System"
                  />
                </div>

                <div className="mb-3 me-2">
                  <CommonSelect
                    className="select"
                    options={[{ _id: "", name: "All Fee Groups" }, ...feeGroups]}
                    value={
                      filters.feeGroup
                        ? feeGroups.find((fg) => fg._id === filters.feeGroup) || null
                        : { _id: "", name: "All Fee Groups" }
                    }
                    getOptionValue={(item: any) => item._id}
                    getOptionLabel={(item: any) => item.name}
                    onChange={(item: any) =>
                      setFilters((prev) => ({ ...prev, feeGroup: item?._id || undefined }))
                    }
                    placeholder="All Fee Groups"
                  />
                </div>

                <div className="mb-3 me-2">
                  <CommonSelect
                    className="select"
                    options={[{ _id: "", firstName: "All", lastName: "Cashiers" }, ...cashiers]}
                    value={
                      filters.collectedBy
                        ? cashiers.find((c) => c._id === filters.collectedBy) || null
                        : { _id: "", firstName: "All", lastName: "Cashiers" }
                    }
                    getOptionValue={(item: any) => item._id}
                    getOptionLabel={(item: any) =>
                      `${item.firstName || ""} ${item.lastName || ""}`.trim()
                    }
                    onChange={(item: any) =>
                      setFilters((prev) => ({ ...prev, collectedBy: item?._id || undefined }))
                    }
                    placeholder="All Cashiers"
                  />
                </div>
              </div>
            </div>

            <div className="card-body">
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-2">Loading report...</p>
                </div>
              ) : (
                <CashierReportTables totals={totals} />
              )}
            </div>
          </div>
        </div>
      </div>

      <InvoicesPrintModal
        show={showPrintModal}
        onClose={() => setShowPrintModal(false)}
        invoices={invoices}
        printRef={printRef}
        onPrint={() => {}}
        filters={{
          startDate: filters.startDate,
          endDate: filters.endDate,
          system: filters.system,
          session: filters.session,
        }}
        exportType={exportType}
      />
    </div>
  )
}

export default CashierReport
